import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import StickButtonsBloc from '../../blocs/common/StickButtonsBloc';
import { APP_URLS, ERROR_MESSAGES, MAX_SMALL_MOBILE_WIDTH } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import { IApiErrors, IExtractedRibInfo, IUploadedDocument } from '../../context/model';
import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import MgpFormCheckbox from '../../elements/MgpFormCheckbox/MgpFormCheckbox';
import LoaderModal from '../../modals/common/LoaderModal';
import UploadFilePreviewModal from '../../modals/common/UploadFilePreviewModal';
import { deleteDocument, uploadRib, downloadUploadedDocument } from "../../services/CustomerApiService";
import { accessJsonAttr, getCompressFile, getFilename, isAcceptedFileSize, isValidFileSize, isValidFileType, isValidIBAN } from '../../utils/utils';
import QrCodeModal from '../../modals/common/QrCodeModal';
import { isPropositionInLock } from '../../utils/dataUtils';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import UploadDocumentNotice from '../common/UploadDocumentNotice';
import ListOfFileUploaded from '../common/ListOfFileUploaded';
import MgpInputFileUpload from '../../elements/MgpInputFileUpload/MgpInputFileUpload';
import UploadFromPhoneButton from '../common/UploadFromPhoneButton';

type InfoPersoStep4FormData = {
  iban: string;
  bankingHolder: string;
  attachRibId: string | null;
  ribHolderConfirm: boolean;
}

export default function InfoPersoStep4FormComponent() {

  const {
    state: { proposition, updatedProposition, ribDocument, jwtToken, showModal },
    dispatch,
  } = useAppState();

  const validationSchema = Yup.object().shape({
    bankingHolder: Yup.string().required(ERROR_MESSAGES.requiredMsg) 
                                .matches(/^[a-zA-Z, .'-]+$/, ERROR_MESSAGES.invalidMsg ),
    iban: Yup.string().required(ERROR_MESSAGES.requiredMsg)
                      .test("is-france-IBAN", ERROR_MESSAGES.franceIBANMsg, (value) => {
                        const FRvalid = value?.slice(0,2) === 'FR'
                        return FRvalid;
                      })
                      .test("is-IBAN-valid", ERROR_MESSAGES.invalidIBANMsg, (value) => {
                        const valid = isValidIBAN(value as string, );
                        return valid;
                      }),
    ribHolderConfirm: Yup.bool().oneOf([true],ERROR_MESSAGES.requiredMsg)
  });
  
  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [errorExtractRibMsg, setErrorExtractRibMsg] = useState<string | null>();
  const [errorFileTypeMsg, setErrorFileTypeMsg] = useState<boolean | null>();
  const [errorFileSizeMsg, setErrorFileSizeMsg] = useState<boolean | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [openPreviewFile, setOpenPreviewFile] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [showConfirmUploadButton, setShowConfirmUploadButton] = useState<boolean>(false);
  const navigate = useNavigate();

  const {width} = useWindowDimensions();
  const isDesktop = width > MAX_SMALL_MOBILE_WIDTH;
 
  const showQrCodeModal = () => {
    setIsShowModal(true);
  }
  const closeModal = () => {
    setIsShowModal(false);
  }

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<InfoPersoStep4FormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      bankingHolder: updatedProposition?.bankingHolder ? updatedProposition?.bankingHolder : '',
      iban: updatedProposition?.iban ? updatedProposition.iban.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim(): "",
      attachRibId: ribDocument?.id ? ribDocument?.id : null,
      ribHolderConfirm: updatedProposition?.ribHolderConfirm ? updatedProposition?.ribHolderConfirm: false
    },
    resolver: yupResolver(validationSchema),
  });

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const uploadedfile = event.target.files ? event.target.files[0] : null;
      if (uploadedfile) {
        if (!isValidFileType(uploadedfile)) {
          setErrorFileTypeMsg(true)
          setErrorExtractRibMsg(null)
        } else if (!isValidFileSize(uploadedfile)) {
          setErrorFileSizeMsg(true)
          setErrorExtractRibMsg(null)
        } else if (!isAcceptedFileSize(uploadedfile)) {
          try {
            const token = jwtToken!.accessToken;
            const newFile = await getCompressFile(token, uploadedfile);
            setFile(newFile);
            setOpenPreviewFile(true);
            setShowConfirmUploadButton(true);
            dispatch({type: 'SHOW_MODAL', showModal: true});
          } catch(e) {
            setErrorMsg("Suite à un incident technique, nous ne pouvons pas uploader votre fichier. Veuillez sélectionner à nouveau.");
          }  
        } else {
          setFile(uploadedfile);
          setOpenPreviewFile(true);
          setShowConfirmUploadButton(true);
          dispatch({type: 'SHOW_MODAL', showModal: true});
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  
  };


  const onNextAction = (data: InfoPersoStep4FormData) => {
    let tempUpdatedPropo = updatedProposition;
    if(tempUpdatedPropo) {
      tempUpdatedPropo.iban = data.iban.replace(/ /g, '');
      tempUpdatedPropo.bankingHolder = data.bankingHolder;
      tempUpdatedPropo.ribHolderConfirm = data.ribHolderConfirm;
    }
    let nextUrl = APP_URLS.infoPersoStep5Page;
    dispatch({type: 'NEXT_INFO_STEP', path: nextUrl, updatedProposition: tempUpdatedPropo});
    navigate({
      pathname: nextUrl,
      search: `?id=${proposition!.propositionId}`,
    });
  }

  const onUploadAction = async () => {
    setErrorMsg(null);
    setErrorExtractRibMsg(null);
    setErrorFileTypeMsg(false);
    setErrorFileSizeMsg(false);
    setLoading(true);
    setValue('bankingHolder', '');
    setValue('iban', '');
    try {
      const token = jwtToken!.accessToken;
      const res = await uploadRib(token, proposition!.propositionId, file as File);
      setLoading(false);
      if (res.data) {
        const ribDocument = res.data;
        onUpdatedFormValue(ribDocument);
      } else {
        setErrorMsg(ERROR_MESSAGES.lowQualityDoc);
      }
     
    } catch (error) {
      setLoading(false);
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }

  const onEventMessage = (message: any, type: string) => {
    if(type === 'upload') {
      const ribDocument = message as IUploadedDocument<IExtractedRibInfo>;
      onUpdatedFormValue(ribDocument);
      setIsShowModal(false);
      setShowConfirmUploadButton(false);
    }
  }

  const onUpdatedFormValue = (ribDocument: IUploadedDocument<IExtractedRibInfo>) => {
    if(ribDocument?.extractedData.isValid === true) {
      setValue('bankingHolder', ribDocument?.extractedData.accountOwner, { shouldValidate: true });
      setValue('iban', ribDocument?.extractedData.iban.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim(), { shouldValidate: true });
    } else if(ribDocument?.extractedData.isValid === false){
      setErrorExtractRibMsg("Nous n’avons pas réussi à identifier les informations présentes dans le document. Nous vous invitons à nous le transmettre sous un autre format ou à saisir vos données.")
    } else if(ribDocument?.extractedData.iban === null){
      setErrorExtractRibMsg("Nous n’avons pas réussi à identifier votre IBAN dans ce document, veuillez télécharger une attestation en cours de validité.")
    }
    setValue('attachRibId', ribDocument.id);
    dispatch({type: 'EXTRACT_RIB', ribDocument});
  }

  const onPreviousAction = () => {
    dispatch({type: 'BACK_INFO_STEP', path: APP_URLS.infoPersoStep3Page});
    navigate({
      pathname: APP_URLS.infoPersoStep3Page,
      search: `?id=${proposition!.propositionId}`,
    });
  }

  const downloadDocument = async (documentId: string) => {
    try {
      const token = jwtToken!.accessToken;
      const res = await downloadUploadedDocument(token, proposition!.propositionId, documentId);
      const filename = getFilename(res.headers);
      const file = new File([res.data], filename);
      setFile(file);
      setOpenPreviewFile(true);
      dispatch({type: 'SHOW_MODAL', showModal: true});
      setShowConfirmUploadButton(false);

    } catch (error) {
      setLoading(false);

      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if (err.response && err.response.status === 404) {
        setErrorMsg(ERROR_MESSAGES.documentNotFound);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }

  const onDeleteDocument = async (documentId: string) => {
    setLoading(true);
    try {
      const token = jwtToken!.accessToken;
      await deleteDocument(token, proposition!.propositionId, documentId);
      
      dispatch({type: 'EXTRACT_RIB', ribDocument: null});
      setValue('attachRibId', null);
      setLoading(false);
       setErrorMsg(null);
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if (err.response && err.response.status === 404) {
        dispatch({type: 'EXTRACT_RIB', ribDocument: null});
        setValue('attachRibId', null);
        setErrorMsg(null);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
      setLoading(false);
    }
    
  }


  const displayRibInfo = () => {
    return (<div>
      <div className="">
        <div className='form-row'>
          <div className="form-group mw-100">
            <MgpFormInput
              label="Titulaire du compte"
              id="bankingHolder"
              key={getValues('bankingHolder')}
              labelFontWeight='regular'
              required
              register={register}
              errors={errors}
            />
          </div>
        </div>
        
        <div className='form-row'>
          <div className="form-group mw-100">
            <MgpFormInput
              label="Votre IBAN"
              id="iban"
              key={getValues('iban')}
              labelFontWeight='regular'
              required
              register={register}
              errors={errors}
              autospace={4}
              autoCapitalize='characters'
            />
          </div>
        </div>
      </div>

      {ribDocument?.extractedData && ribDocument?.extractedData.isValid &&
      <div className='mw-100'>
        <UploadDocumentNotice text="Veuillez vérifier que les informations renseignées sont correctes. N'hésitez pas à les corriger si besoin." />
      </div>}

      <div className="mt-xs">
        <div className="form-group form-check mw-100">
          <MgpFormCheckbox id='ribHolderConfirm' label="J'atteste sur l'honneur que je suis le titulaire du compte et qu'il ne s'agit pas d'un compte bancaire professionnel" register={register} errors={errors} required={true}/>
        </div> 
        <div className="form-row">
          {errors && accessJsonAttr(errors, 'ribHolderConfirm') && <p className="error">Veuillez cocher la case</p>}
        </div>
      </div>

    </div>);
  }

  return (
      <form style={{width: '100%'}}>  
        <div className='form-row page-content'>
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
            <div className=''>
              <h2 className='card-title step-title bleu mb-sm'>Etape 1 : Je joins mon relevé d'identité bancaire</h2>
            </div>
            {errorExtractRibMsg && <p className='text bg-menthe blanc mb-sm p-sm mw-100 bg-orange'>{errorExtractRibMsg}</p>}
            <div className='form-row'>
              <div className='form-group mw-100'>
                {errorFileTypeMsg && <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileTypeMsg}</p> }
                {errorFileSizeMsg && <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileSizeMsg}</p> }
              </div>
            </div>

            <div className="form-row">   
              {ribDocument?.id ?
                <div className="form-row">
                  <div className='form-group mw-100'>
                    <ListOfFileUploaded 
                        fileName={ribDocument.name}
                        onDownloadDocument={() => downloadDocument(ribDocument.id)}
                        onDeleteDocument={() => onDeleteDocument(ribDocument.id)}
                    />
                  </div>
                </div>
                :
                <div>
                <p className='text mt-sm'>Téléchargez votre RIB</p>
                <div className="form-row">
                  <div className='form-group mw-45'>
                    <MgpInputFileUpload
                      id="attachRib"
                      onChange={handleFileChange}
                      register={register} 
                      errors={errors}
                      onClick={(e)=> e.currentTarget.value = ''}
                    />
                  </div>
                  {
                    isDesktop && <div className="form-group mw-5 ta-center pt-xl"><p className='fz-lg'>ou</p></div>
                  }
                  {isDesktop && 
                    <div className="form-group mw-45">
                      <UploadFromPhoneButton onClick={() => showQrCodeModal()}/> 
                    </div>}
                </div>
                </div>
              }
            </div>
            
            {errorMsg && <div className='mt-md'><MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/></div>}
            
          </div>

          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
          <div className=''>
              <h2 className='card-title step-title bleu mb-sm'>Etape 2 : Je vérifie ou complète mes informations</h2>
            </div>
            
              {
                ribDocument?.extractedData?.isValid === false && <div className='box-subTitle'><p className='text pb-sm fushia'>Vos informations n'ont pas été renseignées automatiquement, vous pouvez les compléter ici</p></div>
              }
              {
                ribDocument?.extractedData?.isValid === true && <div className='box-subTitle'><p className='text pb-sm'>Vos informations ont été automatiquement renseignées à partir de votre RIB.</p></div>
              }
            
            
            <div className=''>
              {displayRibInfo()}
            </div>
            

            <div className="mt-xl">
              <p className='text fw-medium'><span className='fushia'>(*)</span> Champs obligatoires</p>
            </div>
          </div>
        </div>
        
        {openPreviewFile&&file&&<UploadFilePreviewModal file={file} onUploadAction={onUploadAction} isOpen={openPreviewFile} onCloseModal={() => {setOpenPreviewFile(false);  dispatch({type: 'SHOW_MODAL', showModal: false});}} showButton={showConfirmUploadButton}/>}
        
        {
          !showModal && <StickButtonsBloc disabledPreviousButton={proposition && isPropositionInLock(proposition) ? true : false} showNextButton={true} showPreviousButton={true} previousAction={onPreviousAction} nextAction={handleSubmit(onNextAction)}/>
        }

        {isShowModal&&<QrCodeModal isOpen={isShowModal} onCloseModal={closeModal} docCode="RIB" onEventMessage={onEventMessage}/>}


        {(isSubmitting || loading) && <LoaderModal hasMessage={false}/>}
        
      </form>
  )
}
